import { Camera, X } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { MAX_ATTACHMENT_BYTES } from '@cfl/assets-shared';

import { Button, Label } from '@/components/cargofl-ui';
import { Hint } from '@/components/cargofl-ui';
import { api } from '@/lib/api';
import type { DocumentEntity } from '@/lib/documents-api';

/**
 * Photos chosen BEFORE the record they belong to exists.
 *
 * Registering an asset, issuing one and taking one back are all create-then-attach:
 * there is no id to upload against until the form is submitted. The obvious shape is
 * to make the user save first and then find an upload button on some other screen,
 * and that is exactly the shape nobody ever completes - the laptop is in their hands
 * NOW, and the photo is worth taking NOW.
 *
 * So this holds the files in memory and the form uploads them once it has an id. The
 * person filling it in never learns there were two steps.
 *
 * A plain file input, not drag-and-drop: `capture` opens the camera directly on a
 * phone, which is where a photo of a scratched lid actually comes from.
 */
export function PhotoPicker({
  files,
  onChange,
  label = 'Photos',
  hint,
  max = 5,
}: {
  files: File[];
  onChange: (next: File[]) => void;
  label?: string;
  hint?: string;
  max?: number;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  /**
   * Object URLs for the thumbnails, revoked when the list changes.
   *
   * Without the revoke each re-pick leaks a blob for the lifetime of the tab. It is
   * invisible and harmless once; it is neither after somebody photographs a room.
   */
  const previews = React.useMemo(
    () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [files],
  );
  React.useEffect(
    () => () => previews.forEach((p) => URL.revokeObjectURL(p.url)),
    [previews],
  );

  const add = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Reset immediately, so picking the SAME file again after a rejection still fires
    // a change event. Without this, a retry of the identical file does nothing.
    event.target.value = '';
    if (picked.length === 0) return;

    const room = max - files.length;
    if (room <= 0) {
      toast.error(`That is the limit of ${max} photos.`);
      return;
    }

    // Checked here as a courtesy so an 18 MB photo fails instantly rather than after
    // the upload. The API's limit is the one that counts.
    const tooBig = picked.filter((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig.length > 0) {
      toast.error(
        `${tooBig[0]!.name} is ${(tooBig[0]!.size / 1024 / 1024).toFixed(1)} MB. The limit is ${Math.round(
          MAX_ATTACHMENT_BYTES / 1024 / 1024,
        )} MB.`,
      );
    }

    const usable = picked.filter((f) => f.size <= MAX_ATTACHMENT_BYTES).slice(0, room);
    if (usable.length < picked.length - tooBig.length) {
      toast.warning(`Only ${room} more photo${room === 1 ? '' : 's'} could be added.`);
    }
    if (usable.length > 0) onChange([...files, ...usable]);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      {previews.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {previews.map(({ file, url }, index) => (
            <div key={`${file.name}-${index}`} className="relative">
              <img
                src={url}
                alt={file.name}
                className="size-16 rounded border object-cover"
              />
              <Hint label={`Remove ${file.name}`}><button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
                className="absolute -right-1.5 -top-1.5 rounded-full border bg-background p-0.5 text-muted-foreground shadow-sm hover:text-destructive"
              >
                <X className="size-3" />
              </button></Hint>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={add}
      />
      <Button hint={"Add a photo"}
        type="button"
        size="sm"
        variant="secondary"
        className="h-8"
        disabled={files.length >= max}
        onClick={() => inputRef.current?.click()}
      >
        <Camera className="mr-1.5 size-3.5" />
        {files.length === 0 ? 'Add photos' : 'Add more'}
      </Button>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {hint ?? `Up to ${max}. Attached once this is saved.`}
      </p>
    </div>
  );
}

/**
 * Upload the held files once the record exists.
 *
 * Deliberately never throws. By the time this runs the asset is registered or the
 * handover is recorded - the thing that mattered has already succeeded, and turning a
 * completed handover into an error because one photo failed would be a lie about what
 * happened. Failures are reported and the ids that did land are kept.
 *
 * Sequential rather than Promise.all: these are phone photos over office wifi, and
 * five parallel multipart uploads is how the slowest one times out.
 */
export async function uploadPendingPhotos(
  entity: DocumentEntity,
  entityId: string,
  files: File[],
): Promise<{ uploaded: number; failed: number }> {
  let uploaded = 0;
  let failed = 0;

  for (const file of files) {
    try {
      const form = new FormData();
      form.append('file', file);
      await api.upload(`/documents/${entity}/${entityId}`, form);
      uploaded += 1;
    } catch {
      failed += 1;
    }
  }

  if (failed > 0) {
    toast.warning(
      `${uploaded} photo${uploaded === 1 ? '' : 's'} attached, ${failed} could not be uploaded. Add them again from the asset page.`,
    );
  }

  return { uploaded, failed };
}
